// src/components/CategoryCarousel.js
import React, { useRef, useEffect, useState, useContext } from "react";
import { LanguageContext } from "../context/LanguageContext";
import { useNavigate } from "react-router-dom";
import { getPublicCategories } from "../services/categories.api";
import { getAllProducts } from "../services/products.api";
import { mapBackendProduct } from "../utils/mapProduct";


const TEXT = {
  EN: {
    heading: "SHOP BY CATEGORY",
    categories: [
      "HEELS",
      "SNEAKERS",
      "BOOTS",
      "FLATS & LOAFERS",
      "SANDALS",
      "BAGS & ACCESSORIES",
    ],
  },
  AR: {
    heading: "تسوقي حسب الفئة",
    categories: [
      "كعب",
      "أحذية رياضية",
      "بوت",
      "فلات ولوفر",
      "صنادل",
      "حقائب وإكسسوارات",
    ],
  },
};

const images = [
  "/images/cat-heels.jpg",
  "/images/cat-sneakers.jpg",
  "/images/cat-boots.jpg",
  "/images/cat-flats.jpg",
  "/images/cat-sandals.jpg",
  "/images/cat-bags.jpg",
];

const categoryKeys = [
  "heels",
  "sneakers",
  "boots",
  "flats-loafers",
  "sandals",
  "bags",
];


function CategoryCarousel() {
  const { lang } = useContext(LanguageContext);
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);

  const mappedCategories = categories.map((c, i) => ({
  title: lang === "AR" ? c.name_ar : c.name_en,
  image: c.image || images[i % images.length], // ✅ use first product image
  key: c.slug,
}));


 const looped = [...mappedCategories, ...mappedCategories, ...mappedCategories];
 
  const scrollRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(categories.length);

  const cardWidth = 190;
  const logicalLength = mappedCategories.length;

  const snapToIndex = (i, smooth = true) => {
    scrollRef.current?.scrollTo({
      left: i * cardWidth,
      behavior: smooth ? "smooth" : "auto",
    });
  };

  useEffect(() => {
  async function load() {
    try {
      const cats = await getPublicCategories();

      const enriched = await Promise.all(
        cats.map(async (c) => {
          const products = await getAllProducts({
  category: c.slug,
  limit: 1, // 👈 IMPORTANT
});

const mapped = products.slice(0, 1).map(mapBackendProduct);

return {
  ...c,
  image: mapped[0]?.images?.[0] || "/images/placeholder-product.jpg",
};

        })
      );

      setCategories(enriched);
    } catch (e) {
      console.error(e);
    }
  }

  load();
}, []);




  useEffect(() => {
  if (logicalLength > 0) {
    setCurrentIndex(logicalLength);
    snapToIndex(logicalLength, false);
  }
}, [logicalLength, lang]);


  const scrollLeft = () => {
  const newIndex = currentIndex - 1;
  setCurrentIndex(newIndex);
  snapToIndex(newIndex);

  // 🔁 silent recenter (after animation)
  if (newIndex === logicalLength - 1) {
    setTimeout(() => {
      const resetIndex = logicalLength * 2 - 1;
      setCurrentIndex(resetIndex);
      snapToIndex(resetIndex, false);
    }, 350);
  }
};

const scrollRight = () => {
  const newIndex = currentIndex + 1;
  setCurrentIndex(newIndex);
  snapToIndex(newIndex);

  // 🔁 silent recenter (after animation)
  if (newIndex === logicalLength * 2) {
    setTimeout(() => {
      const resetIndex = logicalLength;
      setCurrentIndex(resetIndex);
      snapToIndex(resetIndex, false);
    }, 350);
  }
};



  const handleScroll = () => {
    if (!scrollRef.current) return;
    const idx = Math.round(scrollRef.current.scrollLeft / cardWidth);
    setCurrentIndex(idx);
  };

  const realActiveIndex = currentIndex % logicalLength;

  const handleCardClick = (key) => {
    navigate(`/products?category=${key}`, {
  state: { from: "home" },
});
  };

  return (
    <div className="w-full mt-6 mb-6 relative">
      <div
        className="flex items-center justify-between px-4 mb-4"
        dir={lang === "AR" ? "rtl" : "ltr"}
      >
        <button onClick={scrollLeft}>
          <span className="text-lg font-bold text-black">
            {lang === "AR" ? "›" : "<"}
          </span>
        </button>

        <h2 className="text-lg md:text-2xl font-bold tracking-wide text-black">
          {TEXT[lang].heading}
        </h2>

        <button onClick={scrollRight}>
          <span className="text-lg font-bold text-black">
            {lang === "AR" ? "‹" : ">"}
          </span>
        </button>
      </div>

      <div
  ref={scrollRef}
  onScroll={handleScroll}
  dir="ltr"
  className="
    flex 
    gap-4
    overflow-x-auto 
    overflow-y-hidden 
    scrollbar-hide
    px-4 
    snap-x 
    snap-mandatory
  "
  style={{ scrollBehavior: "smooth" }}
>
  {looped.map((cat, i) => {
    const logicalIndex = i % logicalLength;
    const isActive = logicalIndex === realActiveIndex;

    return (
      <button
        key={i}
        onClick={() => handleCardClick(cat.key)}
        className="
          snap-center
          flex flex-col items-center
          min-w-[90px]
          select-none
        "
      >
        {/* INSTAGRAM RING */}
        <div
          className={`
            p-[2px] rounded-full
            ${
              isActive
                ? "bg-gradient-to-tr from-pink-500 via-red-500 to-yellow-400"
                : "bg-gray-300"
            }
            transition-all duration-300
          `}
        >
          {/* IMAGE CIRCLE */}
          <div className="bg-white p-[2px] rounded-full">
            <img
              src={cat.image}
              alt={cat.title}
              className="
                w-[70px] h-[70px]
                md:w-[82px] md:h-[82px]
                object-cover
                rounded-full
                transition-transform duration-300
                group-hover:scale-105
              "
            />
          </div>
        </div>

        {/* TITLE */}
        <span
          className="
            mt-2
            text-[11px] md:text-xs
            font-medium
            text-black
            text-center
            truncate
            max-w-[80px]
          "
          dir={lang === "AR" ? "rtl" : "ltr"}
        >
          {cat.title}
        </span>
      </button>
    );
  })}
</div>


      <div className="w-full flex mt-4 px-6 gap-1 justify-center">
        {categories.map((_, i) => (
          <div
            key={i}
            className="h-[3px] rounded-full transition-all duration-300"
            style={{
              width: realActiveIndex === i ? "28px" : "8px",
              backgroundColor:
                realActiveIndex === i ? "#000000" : "#E6CFCB",
            }}
          ></div>
        ))}
      </div>
    </div>
  );
}

export default CategoryCarousel;
